import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { 
  Save as SaveIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const NachaSettingsForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('nachaSettings');
    return saved ? JSON.parse(saved) : {
      // Company Information
      companyName: '',
      companyDiscretionaryData: '',
      companyIdentification: '', // Tax ID or Company ID from bank
      
      // Originating Financial Institution
      originatingDFIIdentification: '', // Bank routing number (8 digits)
      originatingAccountNumber: '',
      
      // NACHA File Settings
      immediateDestination: '', // Usually same as originating routing (with check digit)
      immediateDestinationName: '',
      immediateOrigin: '', // Company's Federal Tax ID
      immediateOriginName: '',
      
      // Transaction Settings
      serviceClassCode: '220', // 200=Mixed, 220=Credits only, 225=Debits only
      standardEntryClass: 'PPD', // PPD, CCD, CTX, WEB, TEL
      companyEntryDescription: 'SETTLEMENT',
      
      // Offset Account (optional - for balanced files)
      offsetAccountEnabled: false,
      offsetRoutingNumber: '',
      offsetAccountNumber: '',
      offsetAccountName: '',
      
      // File Settings
      fileIdModifier: 'A', // A-Z, 0-9 for multiple files per day
      recordSize: '094', // Always 094 for NACHA
      blockingFactor: '10', // Always 10 for NACHA
      formatCode: '1', // Always 1 for NACHA
    };
  });

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateSettings = () => {
    const errors = [];
    
    // Required fields
    if (!settings.companyName) errors.push('Company Name is required');
    if (!settings.companyIdentification) errors.push('Company Tax ID is required');
    if (!settings.originatingDFIIdentification) errors.push('Originating Bank Routing Number is required');
    if (!settings.originatingAccountNumber) errors.push('Originating Account Number is required');
    if (!settings.immediateDestination) errors.push('Immediate Destination is required');
    if (!settings.immediateOrigin) errors.push('Immediate Origin (Company ID) is required');
    
    // Routing number validation (8 or 9 digits)
    if (settings.originatingDFIIdentification && 
        !/^\d{8,9}$/.test(settings.originatingDFIIdentification)) {
      errors.push('Routing number must be 8 or 9 digits');
    }
    
    // Tax ID validation (9 digits, no dashes)
    if (settings.companyIdentification && 
        !/^\d{9}$/.test(settings.companyIdentification.replace(/[- ]/g, ''))) {
      errors.push('Company Tax ID must be 9 digits (Federal Tax ID)');
    }
    
    // Offset account validation
    if (settings.offsetAccountEnabled) {
      if (!settings.offsetRoutingNumber) errors.push('Offset Routing Number is required when offset is enabled');
      if (!settings.offsetAccountNumber) errors.push('Offset Account Number is required when offset is enabled');
      if (!settings.offsetAccountName) errors.push('Offset Account Name is required when offset is enabled');
    }
    
    return errors;
  };

  const handleSave = async () => {
    const errors = validateSettings();
    if (errors.length > 0) {
      setError(errors.join(', '));
      setSuccess('');
      return;
    }
    
    try {
      setLoading(true);
      
      // Clean up data before saving
      const cleanSettings = {
        ...settings,
        companyIdentification: settings.companyIdentification.replace(/[- ]/g, ''),
        originatingDFIIdentification: settings.originatingDFIIdentification.substring(0, 8), // First 8 digits only
      };
      
      // Save to localStorage for now (until backend is ready)
      localStorage.setItem('nachaSettings', JSON.stringify(cleanSettings));
      
      setSuccess('NACHA settings saved successfully!');
      setError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving NACHA settings:', err);
      setError('Failed to save settings. Please try again.');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          NACHA Export Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Configure your NACHA file settings for ACH payment processing. These settings will be used when exporting payment files.
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* Company Information Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary">
              Company Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your company details as registered with your bank
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Name"
              value={settings.companyName}
              onChange={handleChange('companyName')}
              helperText="As registered with your bank (max 16 characters)"
              inputProps={{ maxLength: 16 }}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Tax ID"
              value={settings.companyIdentification}
              onChange={handleChange('companyIdentification')}
              helperText="9-digit Federal Tax ID (EIN) without dashes"
              placeholder="123456789"
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Discretionary Data"
              value={settings.companyDiscretionaryData}
              onChange={handleChange('companyDiscretionaryData')}
              helperText="Optional reference or batch identifier (max 20 characters)"
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Company Entry Description"
              value={settings.companyEntryDescription}
              onChange={handleChange('companyEntryDescription')}
              helperText="Appears on receiver's bank statement (max 10 characters)"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          
          {/* Originating Bank Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
              Originating Bank Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your company's bank account details for originating ACH transactions
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Originating Bank Routing Number"
              value={settings.originatingDFIIdentification}
              onChange={handleChange('originatingDFIIdentification')}
              helperText="8 or 9 digit ABA routing number"
              placeholder="123456789"
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Originating Bank Account Number"
              value={settings.originatingAccountNumber}
              onChange={handleChange('originatingAccountNumber')}
              helperText="Your company's bank account number"
              required
            />
          </Grid>
          
          {/* NACHA File Header Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
              NACHA File Header Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Technical settings for NACHA file generation
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Immediate Destination (Bank Routing)"
              value={settings.immediateDestination}
              onChange={handleChange('immediateDestination')}
              helperText="Usually your bank's routing number with space prefix ( 123456789)"
              placeholder=" 123456789"
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Immediate Destination Name"
              value={settings.immediateDestinationName}
              onChange={handleChange('immediateDestinationName')}
              helperText="Your bank's name (max 23 characters)"
              inputProps={{ maxLength: 23 }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Immediate Origin (Company ID)"
              value={settings.immediateOrigin}
              onChange={handleChange('immediateOrigin')}
              helperText="Company ID assigned by bank (often Tax ID with 1 prefix: 1123456789)"
              placeholder="1123456789"
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Immediate Origin Name"
              value={settings.immediateOriginName}
              onChange={handleChange('immediateOriginName')}
              helperText="Your company name (max 23 characters)"
              inputProps={{ maxLength: 23 }}
            />
          </Grid>
          
          {/* Transaction Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
              Transaction Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define the type of ACH transactions you'll be processing
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Service Class Code</InputLabel>
              <Select
                value={settings.serviceClassCode}
                onChange={handleChange('serviceClassCode')}
                label="Service Class Code"
              >
                <MenuItem value="200">200 - Mixed Debits and Credits</MenuItem>
                <MenuItem value="220">220 - Credits Only (Recommended for Settlements)</MenuItem>
                <MenuItem value="225">225 - Debits Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Standard Entry Class</InputLabel>
              <Select
                value={settings.standardEntryClass}
                onChange={handleChange('standardEntryClass')}
                label="Standard Entry Class"
              >
                <MenuItem value="PPD">PPD - Prearranged Payment (Consumer)</MenuItem>
                <MenuItem value="CCD">CCD - Corporate Credit/Debit (Business)</MenuItem>
                <MenuItem value="CTX">CTX - Corporate Trade Exchange</MenuItem>
                <MenuItem value="WEB">WEB - Internet Initiated</MenuItem>
                <MenuItem value="TEL">TEL - Telephone Initiated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Offset Account Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
              Offset Account (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure an offset account if your bank requires balanced NACHA files
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Offset Routing Number"
              value={settings.offsetRoutingNumber}
              onChange={handleChange('offsetRoutingNumber')}
              helperText="Routing number for offset account"
              disabled={!settings.offsetAccountEnabled}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Offset Account Number"
              value={settings.offsetAccountNumber}
              onChange={handleChange('offsetAccountNumber')}
              helperText="Account number for offset entries"
              disabled={!settings.offsetAccountEnabled}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Offset Account Name"
              value={settings.offsetAccountName}
              onChange={handleChange('offsetAccountName')}
              helperText="Name on the offset account"
              disabled={!settings.offsetAccountEnabled}
            />
          </Grid>
          
          {/* Info Card */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'info.lighter', mt: 2 }}>
              <CardContent>
                <Box display="flex" alignItems="flex-start">
                  <InfoIcon color="info" sx={{ mr: 2, mt: 0.5 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Important Information
                    </Typography>
                    <Typography variant="body2">
                      • <strong>Company Name & Tax ID:</strong> Must match exactly what your bank has on file<br/>
                      • <strong>Company ID (Immediate Origin):</strong> Often your Tax ID with a "1" prefix (e.g., 1 + 123456789)<br/>
                      • <strong>Service Class Code:</strong> Use 220 (Credits Only) for settlement payments<br/>
                      • <strong>Standard Entry Class:</strong> Use PPD for consumer accounts, CCD for business accounts<br/>
                      • <strong>Offset Account:</strong> Only needed if your bank requires balanced files<br/>
                      • Contact your bank's ACH department if you need help with any of these values
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Save Button */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button 
                onClick={handleSave} 
                variant="contained" 
                size="large"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save NACHA Settings
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default NachaSettingsForm;